import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import {
  accessRefused,
  api,
  ApiCallError,
  credentialRefused,
  idempotencyKey,
  instanceRefused,
  unwrap,
  type Credentials,
} from '../api/client'
import { inHeldEra, useSessionEra } from '../documents/held'
import { t } from '../i18n'
import { ErrorNotice, Notice, Pending, formatDateTime } from '../ui'
import {
  beginRead,
  heldLink,
  holdLink,
  keepLink,
  releaseLink,
  scopeOf,
  seal,
  setAcceptance,
  showable,
  tokenFrom,
  useAcceptance,
} from './invitation'
import { useSession } from './session'
import { useDocumentTitle } from './title'

export { invitationLink } from './invitation'

/**
 * Where an invitation link lands, and where it is accepted.
 *
 * **The link is `/invitation#<token>`, and the token does not stay there.** An
 * administrator issuing an invitation is handed the token once and nothing
 * stores it; this is the address it goes into. A fragment is never sent with a
 * request, so opening the link does not put the token in any request line. The
 * page then reads it into this tab's memory and replaces the address with
 * `/invitation`, so it is not left in the address bar or the history entry
 * either. What happens to it after that, and only that:
 *
 * - The read of what the invitation offers sends it in a request **body**, to
 *   `POST /directory/invitations/pending`. A query string is part of the
 *   request URL, and URLs are what access logs, proxies and browser history
 *   commonly keep; a body is not part of the URL. That is a property of the
 *   transport only: what any particular log records is set by its own
 *   configuration, and nothing here claims it. The `GET ...?token=` form this
 *   replaced is gone from the server.
 * - Accepting sends it in a body, as it always did.
 * - The way to the sign-in page and back carries no token. The sign-in link is
 *   `/signin?return_to=/invitation`, and the token waits in memory for the
 *   person to come back. Memory only: a reload, a new tab, a closed browser or
 *   a sign-in through a provider (a whole-page round trip) forgets it, and the
 *   link has to be followed again. The held link is also let go of after half
 *   an hour unused, and when the screen saying it was accepted is left. That
 *   releases the hold and nothing more: until the tab is reloaded or closed,
 *   the token can still sit in this tab's memory under the keys of the read
 *   and the acceptance it was used for.
 *
 * **Signing in comes first, and this page says so rather than bouncing.** Both
 * routes take the caller's identity the way every route on this platform does,
 * including the read of what is on offer. The session gate in front of every
 * other screen would send somebody to `/signin` and drop them on a projects
 * page afterwards, so this page asks them to sign in and is where they return.
 *
 * **Everything is held per session and per link.** The page sits inside the
 * session provider and the query cache every other screen shares, and neither
 * of those protects what it shows on its own. So the read is cached under the
 * session era it was made in, an acceptance is kept under the session and the
 * link that sent it, and the part of the page that shows them is drawn afresh
 * whenever either changes. The era moves on every sign-in, so signing in again
 * with the same credentials is a new session too. An answer one session asked
 * for is never shown to another, and one link's acceptance never lands on
 * another's page.
 *
 * **A refused read is not shown again until a later read succeeds.** An answer
 * stays in the cache beside a refusal, and a transport failure after the
 * refusal would otherwise put it back on the screen. Each read is numbered as
 * it begins, a refusal seals everything up to its own number, and only an
 * answer to a read begun after the seal is shown -- the button with it.
 *
 * **An acceptance whose answer was lost stays that acceptance.** It is held
 * with the key it was sent under for as long as this tab is open, so leaving
 * the page and coming back finds the same operation. Nothing is sent again by
 * itself. "Check again" sends the same acceptance under the same key, which the
 * server answers with what the first one did if it arrived. A refusal of that
 * retry is not read as proof the first one failed, and the page says so and
 * keeps "Check again" there, under the same key, for when access is back.
 *
 * **Who is signed in is on the screen.** The invitation can be accepted only by
 * the person whose email address it was written to, and the server refuses
 * anybody else, so the address they are signed in as is shown beside the
 * button and they can sign out from here.
 */
export function AcceptInvitation() {
  useDocumentTitle(t('invitation.title'))

  const { credentials, me, signOut } = useSession()
  const era = useSessionEra()
  const { pathname, search, hash } = useLocation()
  const navigate = useNavigate()
  const inAddress = tokenFrom(hash)

  // Out of the address as soon as it has been read, and held for the tab.
  useEffect(() => {
    if (inAddress === '') return
    holdLink(inAddress)
    navigate({ pathname, search }, { replace: true })
  }, [inAddress, pathname, search, navigate])

  const token = inAddress || heldLink()
  const refused = credentialRefused(me.error)
  const signedIn = Boolean(credentials) && !refused && Boolean(me.data)

  return (
    <div className="signin">
      <h2 className="page">{t('app.name')}</h2>
      <p className="page-sub">{t('invitation.title')}</p>

      {token === '' ? <Notice tone="bad">{t('invitation.no_link')}</Notice> : null}

      {token !== '' && credentials && !refused && !me.data ? (
        me.error ? (
          <ErrorNotice error={me.error} retry={() => void me.refetch()} />
        ) : (
          <Pending />
        )
      ) : null}

      {token !== '' && (!credentials || refused) ? (
        <div className="card">
          <p>{t('invitation.sign_in_first')}</p>
          <Link className="button-link" to={`/signin?return_to=${encodeURIComponent('/invitation')}`}>
            {t('invitation.sign_in')}
          </Link>
        </div>
      ) : null}

      {token !== '' && signedIn ? (
        <Invitation
          key={scopeOf(era, token)}
          era={era}
          token={token}
          credentials={credentials}
          email={me.data?.person.email ?? ''}
          signOut={signOut}
        />
      ) : null}
    </div>
  )
}

type Accepted = { project_id?: string | null }

/** One link, for one session. Drawn afresh when either changes. */
function Invitation({
  era,
  token,
  credentials,
  email,
  signOut,
}: {
  era: number
  token: string
  credentials: Credentials
  email: string
  signOut: () => void
}) {
  const queries = useQueryClient()
  const scope = scopeOf(era, token)

  const pending = useQuery({
    queryKey: ['invitation', 'pending', token, era],
    // One read per visit. Every read is one more request carrying the token,
    // and nothing about an invitation changes while somebody looks at it.
    staleTime: Infinity,
    // Once, and only for a request that never reached an answer: a refusal or
    // a "not found" is the instance's last word on this link.
    retry: (count, error) => !(error instanceof ApiCallError) && count < 1,
    queryFn: async () => {
      const read = beginRead()
      try {
        const answer = unwrap(
          await api.POST('/directory/invitations/pending', { body: { token } }),
        )
        return { answer, read }
      } catch (error) {
        if (accessRefused(error)) seal(scope, read)
        throw error
      }
    },
  })

  const acceptance = useAcceptance<Accepted>(scope)
  const shown =
    pending.data && showable(scope, pending.data.read) ? pending.data.answer : undefined

  // The accepted screen keeps its link while it shows, and lets go of it when
  // it is left -- only of the hold it kept, never one made since.
  const accepted = acceptance?.state === 'done'
  useEffect(() => {
    if (!accepted) return
    const kept = keepLink(token)
    return () => releaseLink(kept)
  }, [accepted, token])

  function accept(key: string, retried: boolean) {
    void send(queries, { era, scope, token, credentials, key, retried })
  }

  return (
    <>
      {pending.isPending ? <Pending /> : null}
      {pending.error ? (
        <ErrorNotice error={pending.error} retry={() => void pending.refetch()} />
      ) : null}

      {shown ? (
        <div className="card">
          <dl className="pairs">
            <dt>{t('invitation.for')}</dt>
            <dd>{shown.display_name}</dd>
            <dt>{t('invitation.job')}</dt>
            <dd>{shown.project_name || t('invitation.none_named')}</dd>
            <dt>{t('invitation.company')}</dt>
            <dd>{shown.company_name || t('invitation.none_named')}</dd>
            <dt>{t('invitation.good_until')}</dt>
            <dd>{formatDateTime(shown.expires_at)}</dd>
          </dl>

          {acceptance?.state === 'done' ? (
            <>
              <Notice tone="good">{t('invitation.accepted')}</Notice>
              <p>
                {acceptance.result.project_id ? (
                  <Link to={`/p/${acceptance.result.project_id}`}>{t('invitation.open_job')}</Link>
                ) : (
                  <Link to="/">{t('projects.title')}</Link>
                )}
              </p>
            </>
          ) : acceptance?.state === 'sent' ? (
            <Pending />
          ) : acceptance?.state === 'uncertain' ? (
            <>
              <Notice tone="warn">{t('invitation.uncertain')}</Notice>
              <div className="row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => accept(acceptance.key, true)}
                >
                  {t('invitation.check_again')}
                </button>
              </div>
            </>
          ) : acceptance?.state === 'refused_after_uncertain' ? (
            <>
              <ErrorNotice error={acceptance.error} />
              <Notice tone="warn">{t('invitation.refused_after_uncertain')}</Notice>
              <div className="row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => accept(acceptance.key, true)}
                >
                  {t('invitation.check_again')}
                </button>
              </div>
            </>
          ) : acceptance?.state === 'refused' ? (
            <ErrorNotice error={acceptance.error} />
          ) : shown.expired ? (
            <Notice tone="warn">{t('invitation.expired')}</Notice>
          ) : (
            <>
              <p className="hint">{t('invitation.signed_in_as', { email })}</p>
              <div className="row">
                <button
                  type="button"
                  className="primary"
                  onClick={() => accept(idempotencyKey('invitation'), false)}
                >
                  {t('invitation.accept')}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {acceptance?.state === 'done' ? null : (
        <p className="muted">
          <button type="button" className="subtle" onClick={signOut}>
            {t('signin.sign_out')}
          </button>
        </p>
      )}
    </>
  )
}

/**
 * Send an acceptance, and settle it under the session and link that sent it.
 *
 * Outside the component on purpose. The answer can arrive after the page has
 * moved on to another link, another session or another screen, and what it
 * does then is addressed to where it came from: the acceptance it settles is
 * that scope's, so a late answer cannot be shown on a different invitation.
 *
 * And nothing at all once the session that sent it has ended. The era it was
 * sent in is carried through the wait and asked about when the answer lands:
 * if it has moved, the result is not settled anywhere a screen reads and no
 * session read is refreshed, so an old session's answer cannot refresh, or be
 * shown to, whoever is signed in now -- even with the same credentials.
 */
async function send(
  queries: QueryClient,
  {
    era,
    scope,
    token,
    credentials,
    key,
    retried,
  }: {
    era: number
    scope: string
    token: string
    credentials: Credentials
    key: string
    retried: boolean
  },
): Promise<void> {
  if (!inHeldEra(era)) return
  setAcceptance(scope, { state: 'sent', key, retried })
  try {
    const result = unwrap(
      await api.POST('/directory/invitations/accept', { body: { token, idempotency_key: key } }),
    )
    if (!inHeldEra(era)) return
    setAcceptance(scope, { state: 'done', key, result })
    void queries.invalidateQueries({ queryKey: ['session', 'me', credentials] })
  } catch (error) {
    if (!inHeldEra(era)) return
    // A refusal of the first send is definite: nothing was written. A refusal
    // of a retry whose first answer was lost establishes nothing about the
    // first one. Anything else leaves the question open.
    if (!instanceRefused(error)) setAcceptance(scope, { state: 'uncertain', key, error })
    else if (retried) setAcceptance(scope, { state: 'refused_after_uncertain', key, error })
    else setAcceptance(scope, { state: 'refused', key, error })
  }
}
